import { memo, useEffect, useMemo } from "react";
import { Handle, Position, NodeProps, useUpdateNodeInternals } from "reactflow";
import { SquareActivity } from "lucide-react";
import { EventNodeData, HandleConfig } from '../../types';
import { nodeColors } from "../../components/left-panel/constants";

const calculateHandleStyle = (index: number, total: number, handlePosition: Position): React.CSSProperties => {
  const calcOffset = 5;
  const percentage = (index + 1) * (100 / (total + 1));

  switch (handlePosition) {
    case Position.Top:
      return { left: `calc(${percentage}% - ${calcOffset}px)`, top: `-${calcOffset}px` };
    case Position.Bottom:
      return { left: `calc(${percentage}% - ${calcOffset}px)`, bottom: `-${calcOffset}px` };
    case Position.Left:
    case Position.Right:
    default:
      return { top: `calc(${percentage}% - ${calcOffset}px)` };
  }
};

function EventNode({
  id,
  data,
  selected,
}: NodeProps<EventNodeData>) {
  const updateNodeInternals = useUpdateNodeInternals();

  useEffect(() => {
    if (Array.isArray(data.handles)) {
      updateNodeInternals(id);
    }
  }, [id, data.handles, updateNodeInternals]);

  return (
    <div
      className={`
        inline-block min-w-[128px] max-w-[320px] pl-3 pr-4 py-2 rounded-lg bg-white dark:bg-gray-700 border transition-all cursor-pointer
        ${selected
            ? `border-orange-500 outline outline-4 outline-orange-500/20 outline-offset-0 shadow-orange-100`
            : "border-gray-300 dark:border-gray-600"
          }
      `}
    >
      <div className="flex items-center gap-2 node-content w-full">
        <div className={`p-1.5 rounded ${selected ? 'bg-orange-200' : nodeColors["event"].bg}`}>
          <SquareActivity className={`w-4 h-4 ${selected ? 'text-orange-600' : nodeColors["event"].text}`} />
        </div>
        <div className="min-w-0 content-container w-full">
          {data.nameVisibility !== "hidden" && data.name && (
            <h3 className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate w-full">
              {data.name}
            </h3>
          )}

          <div className="labels-container flex flex-wrap gap-x-1 w-full">
            {data.label && data.label.trim() !== "" && data.name && data.labelVisibility !== "hidden" && (
              <span
                className="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium truncate max-w-full bg-orange-100 text-orange-800"
              >
                {data.label}
              </span>
            )}
          </div>
        </div>
      </div>

      {data.notes && data.notes.trim() !== "" && data.notesVisibility !== "hidden" && (
        <div className="mt-2 text-[8px] text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50 px-2 py-1 rounded max-h-[80px] overflow-y-auto whitespace-pre-line">
          {data.notes}
        </div>
      )}

      {useMemo(() => {
        const handles = Array.isArray(data.handles) ? [...data.handles] : [];

        const handlesByPosition = handles.reduce((acc, handle) => {
          if (!acc[handle.position]) {
            acc[handle.position] = [];
          }
          acc[handle.position].push(handle);
          return acc;
        }, {} as Record<Position, HandleConfig[]>);

        if (handles.length > 0) {
          return Object.entries(handlesByPosition).map(([_, positionHandles]) =>
            positionHandles.map((handle, index) => (
              <Handle
                key={`${handle.id}-${handle.color}`}
                id={handle.id}
                type={handle.type}
                position={handle.position as Position}
                className="!w-3 !h-3 !border-2 !border-white dark:!border-gray-700"
                style={{
                  ...calculateHandleStyle(index, positionHandles.length, handle.position),
                  backgroundColor: handle.color,
                }}
              />
            ))
          );
        } else {
          return null;
        }
      }, [data.handles])}
    </div>
  );
}

export default memo(EventNode);
